import type {
  AppType,
  ConfigureNumberForm,
} from 'v2/pages/settings/phone-numbers/configure-number.config'
import { ConvChannels } from 'v2/types/api/conversations'
import type { RPhoneNumber } from 'v2/types/api/settings'
import { SandboxConnectKind } from './sandbox-setup-copy'

/**
 * The number fields the PATCH has to carry through unchanged. The endpoint takes
 * a whole `ConfigureNumberForm`, so anything omitted here is a field we would be
 * blanking on the number as a side effect of attaching something to it.
 */
export type SandboxConnectNumber = Pick<
  RPhoneNumber,
  | 'name_tag'
  | 'outgoing_enabled'
  | 'number_type'
  | 'teams'
  | 'whatsapp_enabled'
  | 'sub_account'
  | 'phlo_ids'
>

/** Step-2 option -> the `app_type` contacto-core stores. */
export const SANDBOX_CONNECT_APP_TYPE: Record<SandboxConnectKind, AppType> = {
  [SandboxConnectKind.agent]: 'agent',
  [SandboxConnectKind.stream]: 'xml',
  [SandboxConnectKind.sip]: 'trunk',
}

/**
 * `'none'` is contacto-core's explicit-unlink sentinel; an empty string means
 * "nothing to unlink". Sending the sentinel for a channel that never had a flow
 * asks the backend to detach something that does not exist, so only send it when
 * there is genuinely a link to drop.
 */
const clearLink = (current: string): string => (current ? 'none' : '')

const linkedFlow = (pn: SandboxConnectNumber | undefined, channel: ConvChannels): string =>
  pn?.phlo_ids?.find((p) => p.channel_name === channel)?.phlo_uuid || ''

/**
 * The PATCH body that attaches exactly one of agent / application / trunk to the
 * sandbox number, and detaches the other three.
 *
 * Why this does not reuse `ConfigureNumber`'s own submit mapping: that one clears
 * `app_id` and `trunk_id` when the type changes but leaves `call_workflow_id` and
 * `msg_workflow_id` carrying whatever was seeded, because its hidden fields keep
 * their values. Switching a number from agent to trunk there leaves the old agent
 * flow attached, so the number answers on the agent while the UI reads "SIP Trunk".
 * Every id is therefore written explicitly below, empty ones included.
 *
 * The modal owns the call channel only - it is titled "what answers the call" -
 * and a sandbox DID is voice-only (`sms_enabled: false`), so a message flow is
 * not something this surface can set. It is still cleared, since an `app_type`
 * of `trunk` with a message agent still attached is the same inconsistency.
 */
export const buildSandboxConnectPayload = (
  kind: SandboxConnectKind,
  selectedId: string,
  pn?: SandboxConnectNumber,
): ConfigureNumberForm => {
  const appType = SANDBOX_CONNECT_APP_TYPE[kind]

  return {
    alias_name: pn?.name_tag || '',
    name_tag: pn?.name_tag || '',
    outgoing_enabled: pn?.outgoing_enabled ?? false,
    team_uuids: pn?.teams?.map((team) => team.id) ?? [],
    is_whatsapp_number: pn?.whatsapp_enabled ?? false,
    sub_account: pn?.sub_account || '',
    app_type: appType,
    app_id: appType === 'xml' ? selectedId : '',
    trunk_id: appType === 'trunk' ? selectedId : '',
    call_workflow_id:
      appType === 'agent' ? selectedId : clearLink(linkedFlow(pn, ConvChannels.CALL)),
    msg_workflow_id: clearLink(linkedFlow(pn, ConvChannels.MESSAGE)),
    // contacto-core derives country_iso from the number itself, but needs the
    // type to resolve the trunk linkage. Mirrors ConfigureNumber's submit.
    ...(appType === 'trunk' && { number_type: pn?.number_type || '' }),
  }
}

/** Inverse of `SANDBOX_CONNECT_APP_TYPE`: what the number already has attached. */
export const sandboxConnectKindFor = (appType: AppType | null): SandboxConnectKind | undefined => {
  if (appType === 'agent') return SandboxConnectKind.agent
  if (appType === 'xml') return SandboxConnectKind.stream
  if (appType === 'trunk') return SandboxConnectKind.sip
  return undefined
}

/**
 * The id already attached for a kind, so reopening the dialog preselects it
 * rather than looking like nothing is connected.
 */
export const sandboxConnectCurrentId = (
  kind: SandboxConnectKind,
  pn?: SandboxConnectNumber & { app_id?: string; trunk_id?: number },
): string | undefined => {
  if (!pn) return undefined
  if (kind === SandboxConnectKind.stream) return pn.app_id || undefined
  if (kind === SandboxConnectKind.sip) return pn.trunk_id ? String(pn.trunk_id) : undefined
  return linkedFlow(pn, ConvChannels.CALL) || undefined
}

/**
 * React key for the Connect dialog.
 *
 * The dialog seeds its selection from `currentId` once per mount, so its
 * identity has to change when EITHER input changes. Keying on the kind alone
 * silently lost the preselection: the number query is cold on a fresh Home load
 * and does not gate the step-2 buttons, so `currentId` can resolve after the
 * dialog is already open.
 *
 * Exported and tested rather than inlined at the call site, because a key is
 * invisible in review and a dropped term fails nothing at runtime.
 */
export const sandboxConnectDialogKey = (kind?: SandboxConnectKind, currentId?: string): string =>
  `${kind ?? 'closed'}:${currentId ?? ''}`
